import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GenerationJob, GenerationResult } from '../../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface JobDetailsPageProps {
  jobId: string;
}

const JobDetailsPage: React.FC<JobDetailsPageProps> = ({ jobId }) => {
  const navigate = useNavigate();
  const [job, setJob] = useState<GenerationJob | null>(null);
  const [results, setResults] = useState<GenerationResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobDetails();
    fetchJobResults();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE}/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      console.error('Failed to fetch job details:', error);
    }
  };

  const fetchJobResults = async () => {
    try {
      const response = await axios.get(`${API_BASE}/jobs/${jobId}/results`);
      setResults(response.data);
    } catch (error) {
      console.error('Failed to fetch job results:', error);
    } finally {
      setLoading(false);
    }
  };

  const cloneJob = async () => {
    if (!job) return;

    try {
      const response = await axios.post(`${API_BASE}/jobs/${jobId}/clone`, {
        job_name: `${job.job_name} (Clone)`
      });

      alert('Job cloned successfully!');
      window.location.href = `/job/${response.data.id}`;
    } catch (error) {
      console.error('Failed to clone job:', error);
      alert('Failed to clone job');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-600">Loading job details...</div>;
  }

  if (!job) {
    return <div className="text-center text-red-600 py-12">Job not found</div>;
  }

  // Determine if job has rejections
  const hasRejections = (job.rejection_count ?? 0) > 0;
  const isPartialSuccess = job.status === 'partial_success';
  const isCompleteFailure = job.status === 'failed' && job.batch_completed === 0;
  const hasContentRejections = hasRejections && job.rejection_reasons && job.rejection_reasons.length > 0;

  // Get status display info
  const getStatusDisplay = () => {
    if (job.status === 'completed') {
      return {
        text: 'Completed',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-200'
      };
    } else if (isPartialSuccess) {
      return {
        text: 'Partial Success',
        color: 'text-orange-600',
        bgColor: 'bg-orange-100',
        borderColor: 'border-orange-200'
      };
    } else if (job.status === 'failed') {
      return {
        text: 'Failed',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        borderColor: 'border-red-200'
      };
    } else if (job.status === 'processing') {
      return {
        text: 'Processing',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        borderColor: 'border-blue-200'
      };
    } else if (job.status === 'cancelled') {
      return {
        text: 'Cancelled',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-200'
      };
    } else {
      return {
        text: 'Queued',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        borderColor: 'border-yellow-200'
      };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-900">{job.job_name}</h2>
        <div className="flex space-x-4">
          <button
            onClick={cloneJob}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-green-700 transition-all"
          >
            Clone Job
          </button>
          <a href="/history" className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors">
            ← Back to History
          </a>
        </div>
      </div>

      {/* View Generated Content Button */}
      {results.length > 0 && (
        <button
          onClick={() => navigate(`/gallery/${jobId}`)}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-3 text-lg font-semibold"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>View Generated Content in Gallery ({results.length})</span>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Job Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <div className="text-sm text-gray-500 mb-1">Status</div>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${statusDisplay.bgColor} ${statusDisplay.color}`}>
              {statusDisplay.text}
            </div>
          </div>

          <div>
            <div className="text-sm text-gray-500 mb-1">Workflow</div>
            <div className="text-lg font-semibold">{job.workflow_name}</div>
          </div>

          <div>
            <div className="text-sm text-gray-500 mb-1">Batch Progress</div>
            <div className="text-lg font-semibold">
              {job.batch_completed}/{job.batch_size}
              {hasRejections && (
                <span className="text-sm text-red-600 ml-2">
                  ({job.rejection_count} rejected)
                </span>
              )}
            </div>
          </div>

          <div>
            <div className="text-sm text-gray-500 mb-1">Duration</div>
            <div className="text-lg font-semibold">
              {job.actual_duration ? `${Math.floor(job.actual_duration / 60)}m ${job.actual_duration % 60}s` : 'N/A'}
            </div>
          </div>

          <div>
            <div className="text-sm text-gray-500 mb-1">Created</div>
            <div className="text-lg font-semibold">
              {new Date(job.created_at).toLocaleDateString()}
            </div>
          </div>

          <div>
            <div className="text-sm text-gray-500 mb-1">Priority</div>
            <div className="text-lg font-semibold">{job.priority}</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Parameters Used</h3>
        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm overflow-auto">
          {JSON.stringify(job.parameters, null, 2)}
        </pre>
      </div>

      {/* Partial Success Notice */}
      {isPartialSuccess && (
        <div className={`${statusDisplay.bgColor} border-2 ${statusDisplay.borderColor} rounded-xl p-6`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-semibold text-orange-800 mb-2">
                Partial Success - Some Images Rejected
              </h3>
              <p className="text-orange-700 mb-3">
                <span className="font-semibold">{job.batch_completed}/{job.batch_size}</span> images generated successfully.{' '}
                <span className="font-semibold">{job.rejection_count}</span> {job.rejection_count === 1 ? 'image was' : 'images were'} rejected by content moderation.
              </p>
              
              {hasContentRejections && (
                <div className="bg-white border border-orange-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Rejected Images:</p>
                  <div className="space-y-2">
                    {job.rejection_reasons!.map((rejection, index) => (
                      <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-orange-900 mb-1">
                              Batch #{rejection.batch_index}
                            </p>
                            <p className="text-sm text-orange-800 font-mono">{rejection.reason}</p>
                          </div>
                          <span className="text-xs text-orange-600 ml-3">
                            {new Date(rejection.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 text-sm text-orange-700">
                <p className="font-semibold mb-1">What this means:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Some variations triggered content moderation policies</li>
                  <li>Successfully generated images are available below</li>
                  <li>You can clone this job and modify parameters to try again</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Failure with Content Rejections */}
      {isCompleteFailure && hasContentRejections && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                Content Policy Violation
              </h3>
              <p className="text-red-700 mb-3">
                All {job.batch_size} {job.batch_size === 1 ? 'image was' : 'images were'} rejected by content moderation.
              </p>
              
              <div className="bg-white border border-red-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Rejection Reasons:</p>
                <div className="space-y-2">
                  {job.rejection_reasons!.map((rejection, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-900 mb-1">
                            Batch #{rejection.batch_index}
                          </p>
                          <p className="text-sm text-red-800 font-mono">{rejection.reason}</p>
                        </div>
                        <span className="text-xs text-red-600 ml-3">
                          {new Date(rejection.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 text-sm text-red-700">
                <p className="font-semibold mb-1">What this means:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>The content violated EOK's acceptable use policies</li>
                  <li>No images were generated for this request</li>
                  <li>You can modify your prompt or parameters and try again</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Technical Failure Warning (non-rejection failures) */}
      {job.status === 'failed' && !hasContentRejections && job.error_message && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Generation Failed
              </h3>
              <div className="bg-white border border-yellow-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Error Message:</p>
                <p className="text-yellow-900 font-mono text-sm">{job.error_message}</p>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default JobDetailsPage;
